import * as THREE from 'three'
import {
  GSPLAT_CLIP_EPSILON,
  type GSplatClipBox,
  type GSplatClipPolygon,
  type GSplatClipState,
  type GSplatClipVolume,
  GSplatClipVolumeKind,
  isPointInGSplatClipPolygon,
} from '@/plugins/gsplat/clipping/GSplatClipState'
import { ClipTask } from '@/shared/constants'

/** 叶节点相对单个裁切体的保守关系。 */
export type GSplatClipNodeRelation = 'inside' | 'outside' | 'intersect'

export type GSplatClipCullKind = 'polygon' | 'box' | 'mixed' | null

/** 组合全部裁切体后得到的叶节点粗裁结果。 */
export interface GSplatClipNodeClassification {
  readonly hidden: boolean
  readonly cullKind: GSplatClipCullKind
  readonly intersectsBoundary: boolean
}

interface ProjectedPoint extends THREE.Vector2 {
  ndcZ: number
}

const UNIT_BOX_MIN = -0.5
const UNIT_BOX_MAX = 0.5

function getBoxCorners(box: THREE.Box3): THREE.Vector3[] {
  const { min, max } = box
  return [
    new THREE.Vector3(min.x, min.y, min.z),
    new THREE.Vector3(max.x, min.y, min.z),
    new THREE.Vector3(min.x, max.y, min.z),
    new THREE.Vector3(max.x, max.y, min.z),
    new THREE.Vector3(min.x, min.y, max.z),
    new THREE.Vector3(max.x, min.y, max.z),
    new THREE.Vector3(min.x, max.y, max.z),
    new THREE.Vector3(max.x, max.y, max.z),
  ]
}

function projectPoint(point: THREE.Vector3, matrix: THREE.Matrix4): ProjectedPoint | null {
  const elements = matrix.elements
  const x = elements[0] * point.x + elements[4] * point.y + elements[8] * point.z + elements[12]
  const y = elements[1] * point.x + elements[5] * point.y + elements[9] * point.z + elements[13]
  const z = elements[2] * point.x + elements[6] * point.y + elements[10] * point.z + elements[14]
  const w = elements[3] * point.x + elements[7] * point.y + elements[11] * point.z + elements[15]

  if (
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(z) ||
    !Number.isFinite(w) ||
    w <= GSPLAT_CLIP_EPSILON
  ) {
    return null
  }

  const ndcX = x / w
  const ndcY = y / w
  const ndcZ = z / w
  if (!Number.isFinite(ndcX) || !Number.isFinite(ndcY) || !Number.isFinite(ndcZ)) {
    return null
  }

  const result = new THREE.Vector2(ndcX, ndcY) as ProjectedPoint
  result.ndcZ = ndcZ
  return result
}

function cross(origin: THREE.Vector2, left: THREE.Vector2, right: THREE.Vector2): number {
  return (left.x - origin.x) * (right.y - origin.y) - (left.y - origin.y) * (right.x - origin.x)
}

function computeConvexHull(points: ReadonlyArray<THREE.Vector2>): THREE.Vector2[] {
  const sorted = points
    .map((point) => point.clone())
    .sort((left, right) => left.x - right.x || left.y - right.y)

  const unique: THREE.Vector2[] = []
  for (const point of sorted) {
    const previous = unique[unique.length - 1]
    if (
      !previous ||
      Math.abs(previous.x - point.x) > GSPLAT_CLIP_EPSILON ||
      Math.abs(previous.y - point.y) > GSPLAT_CLIP_EPSILON
    ) {
      unique.push(point)
    }
  }
  if (unique.length <= 2) return unique

  const lower: THREE.Vector2[] = []
  for (const point of unique) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2]!, lower[lower.length - 1]!, point) <= GSPLAT_CLIP_EPSILON
    ) {
      lower.pop()
    }
    lower.push(point)
  }

  const upper: THREE.Vector2[] = []
  for (let index = unique.length - 1; index >= 0; index -= 1) {
    const point = unique[index]!
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2]!, upper[upper.length - 1]!, point) <= GSPLAT_CLIP_EPSILON
    ) {
      upper.pop()
    }
    upper.push(point)
  }

  lower.pop()
  upper.pop()
  return lower.concat(upper)
}

function orientation(start: THREE.Vector2, end: THREE.Vector2, point: THREE.Vector2): number {
  return cross(start, end, point)
}

function pointOnSegment2D(point: THREE.Vector2, start: THREE.Vector2, end: THREE.Vector2): boolean {
  if (Math.abs(orientation(start, end, point)) > GSPLAT_CLIP_EPSILON) return false
  return (
    point.x >= Math.min(start.x, end.x) - GSPLAT_CLIP_EPSILON &&
    point.x <= Math.max(start.x, end.x) + GSPLAT_CLIP_EPSILON &&
    point.y >= Math.min(start.y, end.y) - GSPLAT_CLIP_EPSILON &&
    point.y <= Math.max(start.y, end.y) + GSPLAT_CLIP_EPSILON
  )
}

function segmentsIntersect(
  firstStart: THREE.Vector2,
  firstEnd: THREE.Vector2,
  secondStart: THREE.Vector2,
  secondEnd: THREE.Vector2
): boolean {
  const firstA = orientation(firstStart, firstEnd, secondStart)
  const firstB = orientation(firstStart, firstEnd, secondEnd)
  const secondA = orientation(secondStart, secondEnd, firstStart)
  const secondB = orientation(secondStart, secondEnd, firstEnd)

  if (
    ((firstA > GSPLAT_CLIP_EPSILON && firstB < -GSPLAT_CLIP_EPSILON) ||
      (firstA < -GSPLAT_CLIP_EPSILON && firstB > GSPLAT_CLIP_EPSILON)) &&
    ((secondA > GSPLAT_CLIP_EPSILON && secondB < -GSPLAT_CLIP_EPSILON) ||
      (secondA < -GSPLAT_CLIP_EPSILON && secondB > GSPLAT_CLIP_EPSILON))
  ) {
    return true
  }

  return (
    pointOnSegment2D(secondStart, firstStart, firstEnd) ||
    pointOnSegment2D(secondEnd, firstStart, firstEnd) ||
    pointOnSegment2D(firstStart, secondStart, secondEnd) ||
    pointOnSegment2D(firstEnd, secondStart, secondEnd)
  )
}

function pointInConvexHull(point: THREE.Vector2, hull: ReadonlyArray<THREE.Vector2>): boolean {
  if (hull.length === 0) return false
  if (hull.length === 1) return point.distanceToSquared(hull[0]!) <= GSPLAT_CLIP_EPSILON ** 2
  if (hull.length === 2) return pointOnSegment2D(point, hull[0]!, hull[1]!)

  let sign = 0
  for (let index = 0; index < hull.length; index += 1) {
    const value = orientation(hull[index]!, hull[(index + 1) % hull.length]!, point)
    if (Math.abs(value) <= GSPLAT_CLIP_EPSILON) continue
    const currentSign = Math.sign(value)
    if (sign !== 0 && sign !== currentSign) return false
    sign = currentSign
  }
  return true
}

function forEachEdge(
  points: ReadonlyArray<THREE.Vector2>,
  callback: (start: THREE.Vector2, end: THREE.Vector2) => boolean
): boolean {
  if (points.length < 2) return false
  const edgeCount = points.length === 2 ? 1 : points.length
  for (let index = 0; index < edgeCount; index += 1) {
    if (callback(points[index]!, points[(index + 1) % points.length]!)) return true
  }
  return false
}

function polygonsHaveEdgeIntersection(
  left: ReadonlyArray<THREE.Vector2>,
  right: ReadonlyArray<THREE.Vector2>
): boolean {
  return forEachEdge(left, (leftStart, leftEnd) =>
    forEachEdge(right, (rightStart, rightEnd) =>
      segmentsIntersect(leftStart, leftEnd, rightStart, rightEnd)
    )
  )
}

/** 保守分类本地叶节点相对 screen-space polygon 的关系。 */
export function classifyGSplatNodeAgainstPolygon(
  localBounds: THREE.Box3,
  matrixWorld: THREE.Matrix4,
  polygon: GSplatClipPolygon
): GSplatClipNodeRelation {
  if (localBounds.isEmpty()) return 'outside'

  const worldViewProjection = polygon.worldViewProjection.clone().multiply(matrixWorld)
  const projected: ProjectedPoint[] = []
  for (const corner of getBoxCorners(localBounds)) {
    const point = projectPoint(corner, worldViewProjection)
    if (!point || point.ndcZ < -1 - GSPLAT_CLIP_EPSILON || point.ndcZ > 1 + GSPLAT_CLIP_EPSILON) {
      return 'intersect'
    }
    projected.push(point)
  }

  const hull = computeConvexHull(projected)
  if (hull.length === 0) return 'intersect'

  const hasEdgeIntersection = polygonsHaveEdgeIntersection(hull, polygon.vertices)
  const everyHullPointInside = hull.every((point) => isPointInGSplatClipPolygon(point, polygon))
  if (everyHullPointInside && !hasEdgeIntersection) {
    return 'inside'
  }

  if (hasEdgeIntersection) return 'intersect'
  if (hull.some((point) => isPointInGSplatClipPolygon(point, polygon))) return 'intersect'
  if (polygon.vertices.some((point) => pointInConvexHull(point, hull))) return 'intersect'
  return 'outside'
}

/** 保守分类本地叶节点相对 world-space Box 的关系。 */
export function classifyGSplatNodeAgainstBox(
  localBounds: THREE.Box3,
  matrixWorld: THREE.Matrix4,
  box: GSplatClipBox
): GSplatClipNodeRelation {
  if (localBounds.isEmpty()) return 'outside'

  const localToBox = box.worldToBox.clone().multiply(matrixWorld)
  let minX = Number.POSITIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let minZ = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY
  let maxZ = Number.NEGATIVE_INFINITY
  let allInside = true

  for (const corner of getBoxCorners(localBounds)) {
    corner.applyMatrix4(localToBox)
    if (!Number.isFinite(corner.x) || !Number.isFinite(corner.y) || !Number.isFinite(corner.z)) {
      return 'intersect'
    }

    minX = Math.min(minX, corner.x)
    minY = Math.min(minY, corner.y)
    minZ = Math.min(minZ, corner.z)
    maxX = Math.max(maxX, corner.x)
    maxY = Math.max(maxY, corner.y)
    maxZ = Math.max(maxZ, corner.z)
    allInside &&=
      corner.x >= UNIT_BOX_MIN - GSPLAT_CLIP_EPSILON &&
      corner.x <= UNIT_BOX_MAX + GSPLAT_CLIP_EPSILON &&
      corner.y >= UNIT_BOX_MIN - GSPLAT_CLIP_EPSILON &&
      corner.y <= UNIT_BOX_MAX + GSPLAT_CLIP_EPSILON &&
      corner.z >= UNIT_BOX_MIN - GSPLAT_CLIP_EPSILON &&
      corner.z <= UNIT_BOX_MAX + GSPLAT_CLIP_EPSILON
  }

  if (allInside) return 'inside'
  if (
    maxX < UNIT_BOX_MIN - GSPLAT_CLIP_EPSILON ||
    minX > UNIT_BOX_MAX + GSPLAT_CLIP_EPSILON ||
    maxY < UNIT_BOX_MIN - GSPLAT_CLIP_EPSILON ||
    minY > UNIT_BOX_MAX + GSPLAT_CLIP_EPSILON ||
    maxZ < UNIT_BOX_MIN - GSPLAT_CLIP_EPSILON ||
    minZ > UNIT_BOX_MAX + GSPLAT_CLIP_EPSILON
  ) {
    return 'outside'
  }
  return 'intersect'
}

export function classifyGSplatNodeAgainstVolume(
  localBounds: THREE.Box3,
  matrixWorld: THREE.Matrix4,
  volume: GSplatClipVolume
): GSplatClipNodeRelation {
  return volume.kind === GSplatClipVolumeKind.BOX
    ? classifyGSplatNodeAgainstBox(localBounds, matrixWorld, volume)
    : classifyGSplatNodeAgainstPolygon(localBounds, matrixWorld, volume)
}

function mergeCullKind(
  current: Exclude<GSplatClipCullKind, null>,
  volume: GSplatClipVolume
): Exclude<GSplatClipCullKind, null> {
  const next = volume.kind === GSplatClipVolumeKind.BOX ? 'box' : 'polygon'
  return current === next ? current : 'mixed'
}

/** 按精裁的 group/task 规则判断整个叶节点是否必定隐藏。 */
export function classifyGSplatClipNode(
  state: GSplatClipState,
  localBounds: THREE.Box3,
  matrixWorld: THREE.Matrix4
): GSplatClipNodeClassification {
  if (state.volumes.length === 0) {
    return { hidden: false, cullKind: null, intersectsBoundary: false }
  }

  const insideGroups = new Map<number, { allOutside: boolean; kind: 'polygon' | 'box' | 'mixed' }>()
  let intersectsBoundary = false

  for (const volume of state.volumes) {
    if (volume.clipTask === ClipTask.HIGHLIGHT) continue
    const relation = classifyGSplatNodeAgainstVolume(localBounds, matrixWorld, volume)
    intersectsBoundary ||= relation === 'intersect'

    if (volume.clipTask === ClipTask.SHOW_OUTSIDE && relation === 'inside') {
      return {
        hidden: true,
        cullKind: volume.kind === GSplatClipVolumeKind.BOX ? 'box' : 'polygon',
        intersectsBoundary,
      }
    }

    if (volume.clipTask !== ClipTask.SHOW_INSIDE) continue
    const kind = volume.kind === GSplatClipVolumeKind.BOX ? 'box' : 'polygon'
    const group = insideGroups.get(volume.groupIndex)
    if (!group) {
      insideGroups.set(volume.groupIndex, { allOutside: relation === 'outside', kind })
      continue
    }
    group.allOutside &&= relation === 'outside'
    group.kind = mergeCullKind(group.kind, volume)
  }

  for (const group of insideGroups.values()) {
    if (group.allOutside) {
      return { hidden: true, cullKind: group.kind, intersectsBoundary }
    }
  }

  return { hidden: false, cullKind: null, intersectsBoundary }
}
